import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
}

type NavItem = {
  text: string;
  path: string;
  adminOnly?: boolean;
  requiredPermission?: string;
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems: NavItem[] = [
    { text: 'Dashboard', path: '/dashboard' },
    { text: 'Dokumentation', path: '/documentation' },
    { text: 'Analyse', path: '/analysis', requiredPermission: 'asm-manage-analytics' },
    { text: 'Admin', path: '/admin', adminOnly: true },
  ];

  return (
    <div className="portal-app">
      <header className="portal-header">
        <div className="portal-header-inner">
          <div className="portal-brand">
            <img
              src={isMobile ? '/images/logo_square.png' : '/images/logo_landscape.png'}
              alt="ASMonitor Portal"
              style={{ height: isMobile ? 40 : 30, width: 'auto' }}
            />
          </div>

          <nav className="portal-nav">
            {menuItems.map((item) => {
              const hasSuperAdminPermission = user?.permissions?.['firehub-super-admin'];
              const hasAdminPermissions =
                user?.permissions?.['asm-manage-users'] ||
                user?.permissions?.['asm-manage-roles'] ||
                user?.permissions?.['asm-settings-permission'];
              const isAdmin = user?.role === 'admin';
              const hasRequiredPermission = item.requiredPermission
                ? !!(user?.permissions as Record<string, boolean> | undefined)?.[item.requiredPermission]
                : true;

              if (item.adminOnly && !hasSuperAdminPermission && !hasAdminPermissions && !isAdmin) return null;
              if (item.requiredPermission && !hasSuperAdminPermission && !hasRequiredPermission) return null;

              const isActive = location.pathname === item.path;

              return (
                <button
                  key={item.text}
                  type="button"
                  onClick={() => navigate(item.path)}
                  className={`portal-nav-link${isActive ? ' is-active' : ''}`}
                >
                  {item.text}
                </button>
              );
            })}

            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="portal-user-btn"
              >
                {user?.username || 'Benutzer'}
                <span style={{ opacity: 0.8, fontSize: 10 }}>▼</span>
              </button>

              {dropdownOpen && (
                <div className="portal-dropdown">
                  <div className="portal-dropdown-meta">
                    {user?.fdName || user?.orgName || 'Organisation'}
                  </div>
                  <button type="button" onClick={handleLogout} className="portal-dropdown-action">
                    Abmelden
                  </button>
                </div>
              )}
            </div>
          </nav>
        </div>
      </header>

      <main className="portal-main">{children}</main>

      {dropdownOpen && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 999 }}
          onClick={() => setDropdownOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;
